// src/store/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api"; // 確保你有封裝 axios 或 fetch

// --- Async Thunks --- //
export const login = createAsyncThunk(
  "auth/login",
  async ({ account, password, remember }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/api/login",
        { account, password, remember },
        { validateStatus: (status) => status < 500 }
      );

      if (res.data.error !== 0) {
        return rejectWithValue(res.data.msg || "登入失敗");
      }
      return res.data; // 返回使用者資料
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || "伺服器錯誤");
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async () => {
  await api.post("/api/logout");
  return null; // 清除使用者資訊
});

export const auth = createAsyncThunk(
  "auth/check",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/auth");
      if (res?.data?.error === 0 && res?.data?.data) {
        return res?.data?.data;
      } else {
        return rejectWithValue(res.data.msg || "尚未登入");
      }
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.response?.data?.msg || "驗證失敗");
    }
  }
);

// --- Slice --- //
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    loading: false,
    error: null,
    isAuth: false,
    msg: [], // 警報訊息陣列
  },
  reducers: {
    setMsg: (state, action) => {
      state.msg = action.payload;
    },
    addMsg: (state, action) => {
      state.msg.push(action.payload);
    },
    clearMsg: (state) => {
      state.msg = [];
    },
    removeMsg: (state, action) => {
      // 用code或index移除警報訊息
      const target = action.payload;
      if (typeof target === 'string') {
        state.msg = state.msg.filter((m) => m.code !== target);
      } else if (typeof target === 'number') {
        if (target >= 0 && target < state.msg.length) {
          state.msg.splice(target, 1);
        }
      }
    },
  },
  extraReducers: (builder) => {
    // login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuth = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuth = false;
      });

    // logout
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.isAuth = false;
      state.error = null;
    });

    // auth
    builder
      .addCase(auth.pending, (state) => {
        state.loading = true;
      })
      .addCase(auth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuth = true;
      })
      .addCase(auth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuth = false;
      });
  },
});

// --- Actions --- //
export const { setMsg, addMsg, clearMsg, removeMsg } = authSlice.actions;

// --- Selectors --- //
export const selectAuth = (state) => state.auth.isAuth;
export const selectUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectMsg = (state) => state.auth.msg;

export default authSlice.reducer;
